import { NativeEventEmitter, NativeModules, Platform } from 'react-native';

type AndroidModule = {
  start: (title: string, text: string) => Promise<boolean>;
  stop: () => Promise<boolean>;
};

export type IosLocationStatus = {
  status: 'notDetermined' | 'denied' | 'restricted' | 'whenInUse' | 'always';
  servicesEnabled: boolean;
  backgroundCapable: boolean;
};

export type NativeFix = {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude: number;
  speed: number;
  heading: number;
  timestamp: number;
};

type IosModule = {
  getStatus: () => Promise<IosLocationStatus>;
  requestPermission: () => Promise<IosLocationStatus>;
  start: (options: { highAccuracy: boolean; background: boolean }) => Promise<{ background: boolean }>;
  stop: () => Promise<boolean>;
  getCurrentPosition: (options: { maximumAge: number }) => Promise<NativeFix>;
  addListener: (eventName: string) => void;
  removeListeners: (count: number) => void;
};

function androidModule(): AndroidModule | null {
  if (Platform.OS !== 'android') return null;
  const native = NativeModules.LocationTrackingService as Partial<AndroidModule> | undefined;
  return native?.start && native.stop ? (native as AndroidModule) : null;
}

/** iOS tracking module; null on other platforms or an app binary built before it existed. */
export function iosLocationModule(): IosModule | null {
  if (Platform.OS !== 'ios') return null;
  const native = NativeModules.LocationTrackingService as Partial<IosModule> | undefined;
  return native?.start && native.requestPermission && native.getCurrentPosition
    ? (native as IosModule)
    : null;
}

let iosEmitter: NativeEventEmitter | null = null;

/** Subscribes to iOS tracking updates and errors; returns the unsubscribe function. */
export function subscribeIosLocation(
  onFix: (fix: NativeFix) => void,
  onError: (error: { code: number; message?: string }) => void,
): () => void {
  const native = iosLocationModule();
  if (!native) return () => undefined;
  iosEmitter ??= new NativeEventEmitter(native as never);
  const subscriptions = [
    iosEmitter.addListener('LocationTrackingUpdate', onFix as never),
    iosEmitter.addListener('LocationTrackingError', onError as never),
  ];
  return () => subscriptions.forEach(subscription => subscription.remove());
}

/**
 * Android only: runs a location foreground service (persistent notification)
 * so the OS keeps delivering fixes while the app is in the background.
 * Resolves false when the service cannot be started; tracking then continues
 * while the app is open.
 */
export async function startLocationForegroundService(
  title: string,
  text: string,
): Promise<boolean> {
  const native = androidModule();
  if (!native) return false;
  try {
    return (await native.start(title, text)) === true;
  } catch {
    return false;
  }
}

export async function stopLocationForegroundService(): Promise<void> {
  const native = androidModule();
  if (!native) return;
  try {
    await native.stop();
  } catch {
    // Already stopped.
  }
}
